import { startOfDay } from 'date-fns'
import { useEffect, useRef } from 'react'

/**
 * 定期運行カレンダーの基準日同期 (#5232)。
 *
 * カレンダーを開いている間に発送日が変わったら、選択済みの日付を捨てて新しい
 * 発送日 1 件だけに戻す。発送日が変わると選択の意味が変わるためである。
 *
 * ■ 「開いた瞬間」には reset しない
 * 開く操作そのものが基準日を選択済みにしている（`toggleCreateCalendar`）。
 * ここで重ねて reset すると、reset が effect のタイミングで走るぶん
 * 「開く → 日付をクリック → その後に effect が走って選択が消える」順序が起きうる。
 * React は commit 後に effect を流すので、commit で描画されたセルを即座に
 * クリックできる自動操作や素早い手動操作は、この隙間に入り込める。
 * よって enabled が false → true になった回は基準日を記録するだけにして、
 * 「開いたあとの発送日変更」だけを reset の対象にする。
 *
 * @param enabled カレンダーを開いていて、かつ reset の対象となるモードか
 * @param shippingDate 現在の発送日。未確定なら null
 * @param onReset 基準日 1 件へ戻すときに呼ばれる
 */
export const useRegularDatesBaseSync = ({
  enabled,
  shippingDate,
  onReset
}: {
  enabled: boolean
  shippingDate: Date | string | null | undefined
  onReset: (base: Date) => void
}): void => {
  // 直近に同期した基準日 (epoch ms)。null は「カレンダーを開いていない」
  const lastBaseRef = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled) {
      lastBaseRef.current = null
      return
    }
    if (!shippingDate) return

    const base = startOfDay(new Date(shippingDate))
    const key = base.getTime()
    if (Number.isNaN(key)) return

    // 開いた直後は記録だけ。選択済みの基準日を上書きしない
    if (lastBaseRef.current === null) {
      lastBaseRef.current = key
      return
    }
    if (lastBaseRef.current === key) return

    lastBaseRef.current = key
    onReset(base)
  }, [enabled, shippingDate, onReset])
}
